import { createReducer, on } from '@ngrx/store';

import { appActions } from '../../app.actions';

import { initialAuthState, AuthStateModel } from './auth-state.model';
import { AuthActions, TotpActions } from './auth.actions';
import { PhoneNumberActions } from './change-phone-number/change-phone-number.actions';
import { LoginActions } from './login/login.actions';
import { LogOutActions } from './logout/logout.actions';
import { UserAccountActions } from './user-account/user-account.actions';

export const AuthStateReducer = createReducer(
  initialAuthState,
  on(
    appActions.setTier1Data,
    (state, { tier1Data }): AuthStateModel => ({
      ...state,
      authProvider: tier1Data?.authProvider ?? null,
    })
  ),
  on(
    AuthActions.setAccessTokenFromSession,
    AuthActions.setAccessTokenFromLogin,
    (state, { token }): AuthStateModel => ({
      ...state,
      accessToken: token,
      tokenRefreshInProgress: false,
      isAuthenticated: !!token,
    })
  ),
  on(
    LoginActions.logIn,
    LoginActions.logInWithMicrosoftSSOResponse,
    LoginActions.logInWithSsoResponse,
    (state): AuthStateModel => ({
      ...state,
      loginInProgress: true,
    })
  ),
  on(
    LoginActions.logInWithPassword,
    // Transient like rememberMe: fetchNinetyToken$ reads it, every terminal outcome below clears it.
    (state, { email, rememberMe }): AuthStateModel => ({
      ...state,
      loginInProgress: true,
      emailAddress: email,
      rememberMe,
    })
  ),
  on(
    LoginActions.logInWithPasswordFailed,
    LoginActions.logInWithSsoFailed,
    LoginActions.logInWithMicrosoftFailed,
    LoginActions.resetLoginInProgress,
    (state): AuthStateModel => ({
      ...state,
      loginInProgress: false,
      emailAddress: '',
      rememberMe: undefined,
    })
  ),
  on(
    LoginActions.logInSuccess,
    (state, { response: { tokens } }): AuthStateModel => ({
      ...state,
      accessToken: tokens.access,
      tokenRefreshInProgress: false,
      isAuthenticated: true,
      emailAddress: '',
      rememberMe: undefined,
    })
  ),
  on(
    AuthActions.setRefreshTokens,
    (state, { tokens }): AuthStateModel => ({
      ...state,
      accessToken: tokens?.access ?? null,
      tokenRefreshInProgress: false,
      tokenRefreshError: !tokens?.access,
      isAuthenticated: !!tokens?.access,
    })
  ),
  on(
    AuthActions.setCognitoUserAttributes,
    (state, { attributes }): AuthStateModel => ({
      ...state,
      cognitoUserAttributes: attributes,
    })
  ),
  on(
    AuthActions.setCognitoMfaAttributes,
    (state, { mfaPreference }): AuthStateModel => ({
      ...state,
      cognitoMfaPreference: mfaPreference,
    })
  ),
  on(PhoneNumberActions.sendVerification, (state): AuthStateModel => ({ ...state, mfaSetupInProgress: true })),
  on(
    UserAccountActions.disableMFAFailure,
    UserAccountActions.disableMFASuccess,
    UserAccountActions.enableMFAFailure,
    UserAccountActions.enableMFASuccess,
    PhoneNumberActions.isBlacklisted,
    PhoneNumberActions.updatePersonError,
    PhoneNumberActions.confirmationCodeError,
    TotpActions.userSettingsTotpCodeSuccess,
    TotpActions.userSettingsDeleteTotpAuthenticatorAppSuccess,
    TotpActions.userSettingsChangeQRCodeSuccess,
    (state): AuthStateModel => ({
      ...state,
      mfaSetupInProgress: false,
      updated: new Date(),
    })
  ),
  on(
    LoginActions.logInWithMicrosoftFailed,
    LoginActions.logInFailed,
    LoginActions.logInWithSsoFailed,
    (state): AuthStateModel => ({ ...state, loginInProgress: false, emailAddress: '', rememberMe: undefined })
  ),
  on(
    LogOutActions.initiateLogOut,
    LogOutActions.initiateLogOutWithSuppliedRedirect,
    (state): AuthStateModel => ({
      ...state,
      logoutInProgress: true,
      isAuthenticated: false,
      loginInProgress: false,
    })
  ),
  on(
    LogOutActions.apiLoggedOut,
    (state): AuthStateModel => ({ ...state, accessToken: null, logoutInProgress: false, loginInProgress: false })
  )
);
